import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
    Box, Container, Paper, Typography, TextField, Button, Link,
    Alert, InputAdornment, IconButton, CircularProgress, LinearProgress,
    Grid, Divider, Checkbox, FormControlLabel,
} from '@mui/material';
import {
    Visibility, VisibilityOff, CheckCircle,
    PersonOutlined as PersonIcon,
    EmailOutlined as EmailIcon,
    LockOutlined as LockIcon,
    BusinessOutlined as CompanyIcon,
    PhoneOutlined as PhoneIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const RegisterPage = () => {
    const navigate = useNavigate();
    const { register } = useAuth();

    const [formData, setFormData] = useState({
        username: '',
        email: '',
        name: '',
        password: '',
        confirmPassword: '',
        company: '',
        phone: '',
    });
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError('');
    };

    const getPasswordStrength = (password) => {
        let strength = 0;
        if (password.length >= 8) strength += 25;
        if (password.length >= 12) strength += 25;
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
        if (/\d/.test(password)) strength += 15;
        if (/[^a-zA-Z\d]/.test(password)) strength += 10;
        return Math.min(strength, 100);
    };

    const passwordStrength = getPasswordStrength(formData.password);

    const getStrengthColor = () => {
        if (passwordStrength < 40) return 'error';
        if (passwordStrength < 70) return 'warning';
        return 'success';
    };

    const getStrengthLabel = () => {
        if (passwordStrength < 40) return 'Weak';
        if (passwordStrength < 70) return 'Medium';
        return 'Strong';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.username.trim()) { setError('Username is required'); return; }
        if (!formData.email.trim())    { setError('Work email is required'); return; }
        if (!formData.name.trim())     { setError('Full name is required'); return; }
        if (!formData.password)        { setError('Password is required'); return; }
        if (!/\S+@\S+\.\S+/.test(formData.email)) { setError('Please enter a valid email address'); return; }
        if (formData.password.length < 8) { setError('Password must be at least 8 characters'); return; }
        if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); return; }
        if (!agreedToTerms) { setError('You must agree to the Terms of Service and Privacy Policy'); return; }

        setLoading(true);
        try {
            const result = await register({
                username: formData.username,
                name: formData.name,
                email: formData.email,
                password: formData.password,
                company: formData.company || undefined,
                phone: formData.phone || undefined,
            });

            if (result.success) {
                setSuccess(true);
            } else {
                setError(result.error || 'Registration failed');
            }
        } catch (err) {
            setError(err.message || 'An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', py: 4 }}>
                <Container maxWidth="sm">
                    <Paper elevation={6} sx={{ p: 5, borderRadius: 3, textAlign: 'center' }}>
                        <CheckCircle sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
                        <Typography variant="h4" fontWeight={700} gutterBottom>Registration Successful!</Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            We've sent a verification email to <strong>{formData.email}</strong>.
                            Please check your inbox and click the verification link to activate your account.
                        </Typography>
                        <Button variant="contained" size="large" onClick={() => navigate('/login')} sx={{ mt: 2, px: 5, borderRadius: 2 }}>
                            Go to Login
                        </Button>
                    </Paper>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', py: 4 }}>
            <Container maxWidth="sm">
                <Paper elevation={6} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3 }}>
                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
                            Create Account
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Join PartzFlow to start ordering components
                        </Typography>
                    </Box>

                    {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

                    <form onSubmit={handleSubmit} noValidate>
                        {/* Username + Work Email */}
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <PersonIcon fontSize="small" color="action" /> Username <span style={{ color: 'red' }}>*</span>
                                </Typography>
                                <TextField
                                    fullWidth size="small"
                                    name="username"
                                    placeholder="Choose a username"
                                    value={formData.username}
                                    onChange={handleChange}
                                    autoFocus
                                    autoComplete="username"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <EmailIcon fontSize="small" color="action" /> Work Email <span style={{ color: 'red' }}>*</span>
                                </Typography>
                                <TextField
                                    fullWidth size="small"
                                    name="email"
                                    type="email"
                                    placeholder="your.name@company.com"
                                    value={formData.email}
                                    onChange={handleChange}
                                    autoComplete="email"
                                />
                            </Grid>
                        </Grid>

                        {/* Full Name */}
                        <Box sx={{ mt: 2.5 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <PersonIcon fontSize="small" color="action" /> Full Name <span style={{ color: 'red' }}>*</span>
                            </Typography>
                            <TextField
                                fullWidth size="small"
                                name="name"
                                placeholder="John Doe"
                                value={formData.name}
                                onChange={handleChange}
                                autoComplete="name"
                            />
                        </Box>

                        {/* Password + Confirm Password */}
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <LockIcon fontSize="small" color="action" /> Password <span style={{ color: 'red' }}>*</span>
                                </Typography>
                                <TextField
                                    fullWidth size="small"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="At least 8 characters"
                                    value={formData.password}
                                    onChange={handleChange}
                                    autoComplete="new-password"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end">
                                                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Typography variant="caption" color="text.secondary">Minimum 8 characters required</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <LockIcon fontSize="small" color="action" /> Confirm Password <span style={{ color: 'red' }}>*</span>
                                </Typography>
                                <TextField
                                    fullWidth size="small"
                                    name="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    placeholder="Confirm your password"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    autoComplete="new-password"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                                    {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                        </Grid>

                        {/* Password strength bar */}
                        {formData.password && (
                            <Box sx={{ mt: 1.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary">Password Strength</Typography>
                                    <Typography variant="caption" color={`${getStrengthColor()}.main`} fontWeight={600}>{getStrengthLabel()}</Typography>
                                </Box>
                                <LinearProgress variant="determinate" value={passwordStrength} color={getStrengthColor()} sx={{ height: 5, borderRadius: 3 }} />
                            </Box>
                        )}

                        <Divider sx={{ my: 3 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Optional Information</Typography>
                        </Divider>

                        {/* Company + Phone */}
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <CompanyIcon fontSize="small" color="action" /> Company
                                </Typography>
                                <TextField
                                    fullWidth size="small"
                                    name="company"
                                    placeholder="Your company name"
                                    value={formData.company}
                                    onChange={handleChange}
                                    autoComplete="organization"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <PhoneIcon fontSize="small" color="action" /> Phone
                                </Typography>
                                <TextField
                                    fullWidth size="small"
                                    name="phone"
                                    type="tel"
                                    placeholder="+1 (555) 123-4567"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    autoComplete="tel"
                                />
                            </Grid>
                        </Grid>

                        {/* Terms checkbox */}
                        <FormControlLabel
                            sx={{ mt: 3, alignItems: 'flex-start' }}
                            control={
                                <Checkbox
                                    checked={agreedToTerms}
                                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                                    sx={{ pt: 0.5 }}
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    I agree to the{' '}
                                    <Link href="#" underline="hover" fontWeight={600}>Terms of Service</Link>
                                    {' '}and{' '}
                                    <Link href="#" underline="hover" fontWeight={600}>Privacy Policy</Link>
                                </Typography>
                            }
                        />

                        {/* Submit */}
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={loading}
                            sx={{ mt: 3, mb: 2, py: 1.4, borderRadius: 2, fontWeight: 700, fontSize: '1rem' }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
                        </Button>

                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                Already have an account?{' '}
                                <Link component={RouterLink} to="/login" fontWeight={700} underline="hover">
                                    Sign In
                                </Link>
                            </Typography>
                        </Box>
                    </form>
                </Paper>
            </Container>
        </Box>
    );
};

export default RegisterPage;
